import React, { useState, useEffect } from "react";
import { Badge } from "./components/ui";
import { SharePopupExact } from "./components/features";
import DesktopHeroLayout from "./components/layout/DesktopHeroLayout";
import TabletHeroLayout from "./components/layout/TabletHeroLayout";
import MobileHeroLayout from "./components/layout/MobileHeroLayout";
import DeviceDebug from "./components/debug/DeviceDebug";
import { useDeviceType } from "./utils/hooks.js";
import {
  handleReferralCode,
  clearReferralCode,
  getUrlReferralCode,
  clearReferralFromUrl,
  resolveLandingView,
  userSession,
} from "./utils";

// Working AppLayout with full desktop layout
const WorkingAppLayout = ({ children }) => {
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: isMobile ? "#FFB6C1" : "#0D0E20",
        backgroundImage: isMobile
          ? "url('/mobile-hero-bg.jpg')"
          : isTablet
            ? "url('/tablet-hero-bg.jpg')"
            : "url('/desktop-hero-bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }}
    >
      {/* Header with logo and early access badge - Skip for tablet and mobile since they handle their own headers */}
      {isDesktop && (
        <header
          style={{
            position: "absolute",
            top: "1.5rem", // Desktop: 24px
            left: "0",
            right: "0",
            zIndex: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 1.5rem", // Desktop: 24px
          }}
        >
          <Badge variant="ffsLogo" size="desktop" />

          <Badge variant="earlyAccess" size="desktop" />
        </header>
      )}

      {children}
    </div>
  );
};

export default function App() {
  // State management
  const [hasJoined, setHasJoined] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralLink, setReferralLink] = useState("");
  const [referredBy, setReferredBy] = useState(null);
  const [queuePosition, setQueuePosition] = useState(0);
  const [referralCount, setReferralCount] = useState(0);
  const [referrals, setReferrals] = useState({ user: null, friend: null });
  const [isClient, setIsClient] = useState(false);

  // Effects
  useEffect(() => {
    setIsClient(true);

    // Try to restore user session first
    const savedSession = userSession.get();
    // Ref from the URL only, vs. handleReferralCode() which also persists it
    // to localStorage and falls back to a previously stored one
    const urlRef = getUrlReferralCode();
    const refCode = handleReferralCode();
    const view = resolveLandingView(savedSession, urlRef);

    if (savedSession) {
      console.log("Restoring user session:", savedSession);
      // Keep the stored identity (code/link/position) so the share view and
      // "Already joined?" still work, but only show the joined state when this
      // visit isn't someone else's referral link opened in the same browser
      setHasJoined(view.hasJoined);
      setReferralCode(savedSession.referralCode || "");
      setReferralLink(savedSession.referralLink || "");
      setQueuePosition(savedSession.queuePosition || 0);
      setReferralCount(savedSession.referralCount || 0);

      // Refresh referral count from API if user has referral code
      if (savedSession.referralCode) {
        refreshReferralCount(savedSession.referralCode);
      }
    }

    // Keep any incoming referral code pending whichever view is showing, so the
    // next signup from this browser (incl. "Not you? Use a different email") is
    // credited to the link's owner. Self-referral can't happen: the owner's
    // email is already registered, so signup returns 409 before any credit.
    if (refCode) {
      setReferredBy(refCode);
      setReferrals((prev) => ({ ...prev, friend: refCode }));
    }
  }, []);

  // Once the share view is showing, drop any referral code from the address
  // bar so a copied URL doesn't pass on someone else's code
  useEffect(() => {
    if (hasJoined) clearReferralFromUrl();
  }, [hasJoined]);

  // Helper function to refresh referral count and queue position from API
  const refreshReferralCount = async (referralCode) => {
    try {
      const response = await fetch(`/api/user/${referralCode}`);
      if (response.ok) {
        const userData = await response.json();
        const newCount = userData.referralCount || 0;
        // Referrals move the user up the queue, so the position changes too
        const newPosition = userData.position || 0;
        setReferralCount(newCount);
        setQueuePosition(newPosition);

        // Update session with fresh count and position
        userSession.update({ referralCount: newCount, queuePosition: newPosition });
        console.log("Updated referral count:", newCount);
      }
    } catch (error) {
      console.error("Failed to refresh referral count:", error);
    }
  };

  // Event handlers
  const handleWaitlistSuccess = (data) => {
    setReferralCode(data.referralCode);
    setReferralLink(data.referralLink);
    setQueuePosition(data.position);
    setReferralCount(data.referralCount || 0);
    setHasJoined(true);
    clearReferralCode();

    // Save user session for persistence
    userSession.save({
      hasJoined: true,
      referralCode: data.referralCode,
      referralLink: data.referralLink,
      queuePosition: data.position,
      referralCount: data.referralCount || 0,
      // Code this signup came through, so reopening that link shows the share view
      referredBy: data.referredBy || null,
      email: data.email, // if available
    });
    console.log("Saved user session after signup");
  };

  const handleReferFriend = async () => {
    // Fetch updated referral count when opening popup
    if (referralCode) {
      await refreshReferralCount(referralCode);
    }
    setShowSharePopup(true);
  };

  const closeSharePopup = () => {
    setShowSharePopup(false);
  };

  // Clear the stored session and show the signup form again ("Not you? Use a
  // different email" + dev Clear Session button). Any pending referral code in
  // referredBy / localStorage is kept so the next signup is still credited.
  const clearUserSession = () => {
    userSession.clear();
    setHasJoined(false);
    setReferralCode("");
    setReferralLink("");
    setQueuePosition(0);
    setReferralCount(0);
    console.log("Cleared user session");
  };

  // Returning user who opened a friend's link but wants their own share view
  const handleShowExistingUser = () => {
    setHasJoined(true);
  };

  // A stored identity exists while the form is showing (friend's link case)
  const hasExistingSession = Boolean(referralCode) && !hasJoined;

  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";

  console.log(
    "App.jsx DEBUG - Width:",
    typeof window !== "undefined" ? window.innerWidth : "undefined",
    "Device type:",
    deviceType,
    "Desktop?",
    isDesktop,
    "isClient:",
    isClient,
  );
  console.log("App.jsx DEBUG - Breakpoints:", {
    DESKTOP: 1200,
    TABLET_LG: 1024,
    TABLET_MD: 834,
  });

  // Always render content, but rely on useDeviceType's hydration handling
  if (!isClient) {
    return <div style={{ minHeight: "100vh", backgroundColor: "#0D0E20" }} />;
  }

  const renderMobileLayout = () => {
    const windowSize =
      typeof window !== "undefined"
        ? { width: window.innerWidth, height: window.innerHeight }
        : { width: 375, height: 812 }; // Default mobile size (iPhone)

    return (
      <MobileHeroLayout
        windowSize={windowSize}
        referrals={referrals}
        setReferrals={setReferrals}
        hasJoined={hasJoined}
        queuePosition={queuePosition}
        onWaitlistSuccess={handleWaitlistSuccess}
        onReferFriend={handleReferFriend}
        referredBy={referredBy}
        referralLink={referralLink}
        referralCount={referralCount}
        hasExistingSession={hasExistingSession}
        onShowExisting={handleShowExistingUser}
        onUseDifferentEmail={clearUserSession}
      />
    );
  };

  const renderTabletLayout = () => {
    const windowSize =
      typeof window !== "undefined"
        ? { width: window.innerWidth, height: window.innerHeight }
        : { width: 768, height: 1024 }; // Default tablet size

    return (
      <TabletHeroLayout
        windowSize={windowSize}
        referrals={referrals}
        setReferrals={setReferrals}
        hasJoined={hasJoined}
        queuePosition={queuePosition}
        onWaitlistSuccess={handleWaitlistSuccess}
        onReferFriend={handleReferFriend}
        referredBy={referredBy}
        referralLink={referralLink}
        referralCount={referralCount}
        hasExistingSession={hasExistingSession}
        onShowExisting={handleShowExistingUser}
        onUseDifferentEmail={clearUserSession}
      />
    );
  };

  const renderDesktopLayout = () => (
    <DesktopHeroLayout
      hasJoined={hasJoined}
      queuePosition={queuePosition}
      onWaitlistSuccess={handleWaitlistSuccess}
      onReferFriend={handleReferFriend}
      referredBy={referredBy}
      hasExistingSession={hasExistingSession}
      onShowExisting={handleShowExistingUser}
      onUseDifferentEmail={clearUserSession}
    />
  );

  const renderContent = () => {
    if (isMobile) return renderMobileLayout();
    if (isTablet) return renderTabletLayout();
    return renderDesktopLayout();
  };

  return (
    <>
      <WorkingAppLayout>{renderContent()}</WorkingAppLayout>

      {/* SharePopup Demo - remove in production */}
      {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <button
            onClick={clearUserSession}
            style={{
              padding: "0.5rem 1rem",
              background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: "bold",
              borderRadius: "0.5rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            🗑️ Clear Session
          </button>
        </div>
      )}

      {showSharePopup && isDesktop && (
        <SharePopupExact
          referralLink={
            referralLink || "https://footyflowstates.com/?ref=demo123"
          }
          referralCount={referralCount}
          onClose={closeSharePopup}
        />
      )}
    </>
  );
}
